import { GoToTeamSettings } from '@/_elements/Links';
import OurAccordion from '@/_elements/OurAccordion';
import { fetchCallsAction, updateCallDataAction } from '@/_redux/actions/ActionCallCenter';
import DownloadBtn from '@/components/ShareBtn/DownloadBtn';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Card, Badge, Dropdown, Row, Col, Button, Form, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    BsClock,
    BsCheckCircle,
    BsXCircle,
    BsExclamationCircle,
    BsTelephoneFill,
    BsTelephoneInboundFill,
    BsTelephoneOutboundFill,
    BsVoicemail,
    BsHeadphones,
    BsPersonPlus,
    BsXSquare,
} from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { connect } from 'react-redux';
import Select from 'react-select';
import { CallStatusBadge } from '../components/CampaignMetricsModal';
import CallCenterNotesModal from '../components/CallCenterNotesModal';
import OpenCallPageButton from '../components/OpenCallPageButton';
import { BootstrapToolTip, ToolTip } from '@/_elements/ToolTip';

export const ContactSelect = ({ value, onChange, contacts }) => (
    <Select
        value={value ? { value: value, label: contacts.find((l) => (l.id || l._id) === value)?.name || '' } : null}
        onChange={(option) => onChange(option ? option.value : '')}
        options={[{ label: 'All', value: '' }].concat(
            contacts.map((contact) => ({
                label: contact.name,
                value: contact.id || contact._id,
            }))
        )}
        placeholder="Contact: All"
        isClearable
    />
);

export const StatusSelect = ({ value, onChange }) => (
    <Select
        value={value ? { value: value, label: value } : null}
        onChange={(option) => onChange(option ? option.value : '')}
        options={[
            { label: 'All', value: '' },
            { label: 'Queued', value: 'queued' },
            { label: 'Ringing', value: 'ringing' },
            { label: 'In Progress', value: 'in_progress' },
            { label: 'Completed', value: 'completed' },
            { label: 'Failed', value: 'failed' },
            { label: 'No Answer', value: 'no_answer' },
            { label: 'Busy', value: 'busy' },
        ]}
        placeholder="Status: All"
        isClearable
    />
);

export const LabelSelect = ({ value, onChange, labels }) => (
    <Select
        value={
            value
                ? {
                      value: value,
                      label: (
                          <div className="d-flex align-items-center">
                              <div
                                  style={{
                                      width: '12px',
                                      height: '12px',
                                      backgroundColor:
                                          labels.find((l) => (l.id || l._id) === value)?.colorHex || '#ccc',
                                      borderRadius: '50%',
                                      marginRight: '8px',
                                  }}
                              ></div>
                              {labels.find((l) => (l.id || l._id) === value)?.name || 'Unknown'}
                          </div>
                      ),
                  }
                : null
        }
        onChange={(option) => onChange(option ? option.value : '')}
        options={[{ label: 'All', value: '' }].concat(
            labels.map((label) => ({
                label: (
                    <div className="d-flex align-items-center">
                        <div
                            style={{
                                width: '12px',
                                height: '12px',
                                backgroundColor: label.colorHex,
                                borderRadius: '50%',
                                marginRight: '8px',
                            }}
                        ></div>
                        {label.name}
                    </div>
                ),
                value: label.id || label._id,
            }))
        )}
        placeholder="Label: All"
        isClearable
    />
);

export const PresetSelect = ({ value, onChange, presets = [] }) => (
    <Select
        value={
            value
                ? {
                      value: value,
                      label: (
                          <div className="d-flex align-items-center">
                              {presets.find((l) => (l.id || l._id) === value)?.title || 'Unknown'}
                          </div>
                      ),
                  }
                : null
        }
        onChange={(option) => onChange(option ? option.value : '')}
        options={[].concat(
            presets.map((preset) => ({
                label: <div className="d-flex align-items-center">{preset.title}</div>,
                value: preset.id || preset._id,
            }))
        )}
        placeholder="Preset: All"
        isClearable
    />
);

const formatDuration = (sec) =>
    sec ? `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}` : '00:00';
const formatDate = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

const StatusIcon = ({ status, size = 44 }) =>
    ({
        queued: <BsClock className="text-warning" size={size} />,
        ringing: <BsTelephoneFill className="text-infosize" size={size} />,
        in_progress: <BsTelephoneFill className="text-primarysize" size={size} />,
        completed: <BsCheckCircle className="text-successsize" size={size} />,
        failed: <BsXCircle className="text-dangersize" size={size} />,
        no_answer: <BsExclamationCircle className="text-secondarysize" size={size} />,
        busy: <BsExclamationCircle className="text-warningsize" size={size} />,
    })[status] || <BsClock size={size} />;

const LabelBadge = ({ labels, labelId }) => {
    if (!labelId)
        return (
            <Badge bg="light" text="dark">
                None
            </Badge>
        );
    const label = labels.find((l) => (l.id || l._id) === labelId);
    if (!label)
        return (
            <Badge bg="light" text="dark">
                -
            </Badge>
        );
    return (
        <Badge
            style={{
                backgroundColor: label.colorHex + '20',
                color: label.colorHex,
                border: `1px solid ${label.colorHex}`,
            }}
            className="px-2 py-1"
        >
            {label.name}
        </Badge>
    );
};

const Component = ({
    labels = [],
    call,
    updateCallDataAction = () => {},
    showTeamData = false,
    index = 0,
    currentPage = 0,
}) => {
    const [isBusy, setIsBusy] = useState(false);
    const [callNote, setCallNote] = useState(call?.callNote || '');
    const rawDirection = String(call?.direction || '').toLowerCase();
    const direction = rawDirection.startsWith('inbound')
        ? 'inbound'
        : rawDirection.startsWith('outbound')
          ? 'outbound'
          : '';
    const missedInboundCall = direction === 'inbound' && ['missed', 'no_answer'].includes(call?.callStatus);
    const DirectionIcon =
        direction === 'inbound' ? BsTelephoneInboundFill : direction === 'outbound' ? BsTelephoneOutboundFill : null;
    const directionIconColor = missedInboundCall ? '#dc3545' : direction === 'inbound' ? '#28A745' : '#00b7f4';
    const contactName = [call?.contactData?.name, call?.contact?.lastName].filter(Boolean).join(' ').trim();
    const callerLabel = contactName || call?.fromNumber || 'Unknown';
    const callerPhone = call?.contactData?.phone || (call?.contact ? call?.fromNumber : '');
    const voicemail = call?.voicemailData;
    const callRecordingUrl = call?.recordingUrl || call?.recordingExternalUrl;
    const showCallRecording = !voicemail || (callRecordingUrl && callRecordingUrl !== voicemail.audioUrl);
    const handleSaveNote = async () => {
        setIsBusy(true);
        await updateCallDataAction(call._id, { callNote });
        setIsBusy(false);
    };
    const handleListenIn = () => {};
    const handleBargeIn = () => {};
    const handleEndCall = () => {};
    return (
        <Card className="mb-3 shadow-sm border-0">
            <OurAccordion
                defaultShow={false}
                titleTag={null}
                title={
                    <div className="d-flex justify-content-between align-items-center w-100 px-3 py-2">
                        <div className="d-flex align-items-center gap-3">
                            <div className="mr-2">{(currentPage - 1) * 10 + index + 1}.</div>
                            <div className="mr-4 d-flex align-items-center">
                                <StatusIcon status={call.callStatus} />
                            </div>

                            <div>
                                {showTeamData && (
                                    <div>
                                        <GoToTeamSettings team={call?.teamData} showAvatar={true} showLink={false} />
                                    </div>
                                )}
                            </div>
                            <div className="ml-4">
                                <div className="fw-bold d-flex align-items-center flex-nowrap">
                                    {DirectionIcon && (
                                        <BootstrapToolTip
                                            text={missedInboundCall ? 'Missed inbound call' : `Direction: ${direction}`}
                                        >
                                            <span className="mr-2" style={{ color: directionIconColor }}>
                                                <DirectionIcon aria-hidden="true" />
                                            </span>
                                        </BootstrapToolTip>
                                    )}
                                    <span className="text-truncate" style={{ minWidth: 0 }}>
                                        {callerLabel}
                                    </span>
                                    {voicemail && (
                                        <BootstrapToolTip text={'Voicemail'}>
                                            <span
                                                className="ml-2"
                                                style={{
                                                    color: 'red',
                                                }}
                                            >
                                                <BsVoicemail size={18} aria-hidden="true" />
                                            </span>
                                        </BootstrapToolTip>
                                    )}
                                </div>

                                <div className="text-muted small">
                                    {call?.contact?.company ? `${call.contact.company} • ` : ''}
                                    {callerPhone || (call?.fromNumber ? '' : 'No phone')}
                                </div>
                            </div>
                        </div>
                        <div
                            className="d-flex align-items-center  text-center small"
                            style={{
                                gap: '20px',
                            }}
                        >
                            <div
                                style={{
                                    textAlign: 'right',
                                }}
                            >
                                <div className="text-muted"></div>
                                <br></br>
                                {dayjs(call?.startedAt || call?.createdAt).format('MMM D, YYYY')}
                            </div>
                            <div
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <div className="text-muted">Started</div>
                                {formatDate(call.startedAt || call.createdAt)}
                            </div>
                            <div
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <div className="text-muted">Duration</div>
                                {formatDuration(call.durationSeconds)}
                            </div>
                            {/* <div
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <div className="text-muted">Preset</div>

                                <div
                                    style={{
                                        width: '80px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {(call.presetData || {})?.title || 'None'}
                                </div>
                            </div> */}
                            <div
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <div className="text-muted">Status</div>
                                <CallStatusBadge status={call.callStatus} />
                            </div>
                            <div
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                <div className="text-muted">Label</div>
                                <LabelBadge labels={labels} labelId={call.followupLabel} />
                            </div>
                        </div>
                    </div>
                }
            >
                <Card.Body
                    style={{
                        boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.16)',
                    }}
                >
                    <Row>
                        <Col md={6}>
                            {voicemail && (
                                <section className="mb-4" aria-label="Voicemail recording">
                                    <div className="d-flex align-items-center mb-2">
                                        <Form.Label className="mb-0 mr-2">Voicemail:</Form.Label>
                                        <Badge
                                            bg={
                                                voicemail.status === 'ready'
                                                    ? 'success'
                                                    : voicemail.status === 'failed'
                                                      ? 'danger'
                                                      : 'secondary'
                                            }
                                        >
                                            {voicemail.status || 'processing'}
                                        </Badge>
                                        {Number.isFinite(Number(voicemail.durationSeconds)) && (
                                            <span className="text-muted small ml-2">
                                                {formatDuration(Number(voicemail.durationSeconds))}
                                            </span>
                                        )}
                                    </div>
                                    {voicemail.audioUrl ? (
                                        <audio
                                            controls
                                            preload="none"
                                            className="w-100"
                                            aria-label={`Voicemail from ${voicemail.callerE164 || callerLabel}`}
                                        >
                                            <source src={voicemail.audioUrl} />
                                            Your browser does not support the audio element.
                                        </audio>
                                    ) : (
                                        <p className="text-muted mb-0">
                                            {voicemail.status === 'failed'
                                                ? 'The voicemail recording could not be saved.'
                                                : 'The voicemail recording is still processing.'}
                                        </p>
                                    )}
                                </section>
                            )}

                            {/* Call Recording */}
                            {showCallRecording && (
                                <>
                                    <div className="d-flex flex-row align-items-center">
                                        <Form.Label>Recording:</Form.Label>
                                        {callRecordingUrl && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center',
                                                    marginLeft: '8px',
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                <DownloadBtn
                                                    url={callRecordingUrl}
                                                    buttonText="Download Recording"
                                                    simpleMode={true}
                                                    size={12}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {callRecordingUrl ? (
                                        <div className="mb-3 d-flex flex-row">
                                            <audio controls className="w-100">
                                                <source src={callRecordingUrl} type="audio/mpeg" />
                                                Your browser does not support the audio element.
                                            </audio>
                                        </div>
                                    ) : (
                                        <div className="mb-3">
                                            <p className="text-muted">No recording available/failed save</p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Call Timeline */}
                            {/* <div className="mb-3">
                                <h6>Timeline</h6>
                                <div className="timeline">
                                    <div className="timeline-event d-flex mb-2">
                                        <Badge bg="info" className="me-2">
                                            STT
                                        </Badge>
                                        <div>Speech-to-Text processing</div>
                                    </div>
                                    <div className="timeline-event d-flex mb-2">
                                        <Badge bg="primary" className="me-2">
                                            LLM
                                        </Badge>
                                        <div>AI response generation</div>
                                    </div>
                                    <div className="timeline-event d-flex mb-2">
                                        <Badge bg="success" className="me-2">
                                            TTS
                                        </Badge>
                                        <div>Text-to-Speech conversion</div>
                                    </div>
                                    {call.status === 'in_progress' && (
                                        <div className="timeline-event d-flex mb-2">
                                            <Badge bg="warning" className="me-2">
                                                LIVE
                                            </Badge>
                                            <div>Call in progress</div>
                                        </div>
                                    )}
                                </div>
                            </div> */}

                            {/* Call Transcript */}
                            <div className="mb-3 w-full mt-4">
                                <Form.Label className="mt-4">Transcript:</Form.Label>
                                {call?.transcription ? (
                                    <textarea style={{ width: '100%', height: '200px' }} disabled>
                                        {(call?.transcriptionData || {})?.text}
                                    </textarea>
                                ) : (
                                    <div className="transcript p-3 border rounded bg-light">
                                        <div className="transcript-entry mb-2">
                                            <span>No transcript available</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* <div className="mb-3">
                                    <h6>Transcript</h6>
                                    {call.transcriptReady ? (
                                        <div className="transcript p-3 border rounded bg-light">
                                            <div className="transcript-entry mb-2">
                                                <Badge bg="secondary" className="me-2">
                                                    AI
                                                </Badge>
                                                <span>
                                                    Hello, this is an AI assistant calling from Optonome. How are you
                                                    today?
                                                </span>
                                            </div>
                                            <div className="transcript-entry mb-2">
                                                <Badge bg="info" className="me-2">
                                                    Contact
                                                </Badge>
                                                <span>I'm doing well, thanks for asking. What's this regarding?</span>
                                            </div>
                                            <div className="transcript-entry mb-2">
                                                <Badge bg="secondary" className="me-2">
                                                    AI
                                                </Badge>
                                                <span>
                                                    I'm calling to follow up on your recent inquiry about our services.
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-muted">Transcript not available</p>
                                    )}
                                </div> */}
                        </Col>

                        <Col md={6}>
                            {/* Follow-up Label */}
                            <div className="mb-3">
                                {/* <h6>Follow-up Label</h6> */}
                                <Dropdown>
                                    {/* <Dropdown.Toggle variant="outline-secondary" className="w-100">
                                        {call.followupLabel ? (
                                            <div className="d-flex align-items-center">
                                                <div
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        backgroundColor:
                                                            labels.find((l) => (l.id || l._id) === call.followupLabel)
                                                                ?.colorHex || '#ccc',
                                                        borderRadius: '50%',
                                                        marginRight: '8px',
                                                    }}
                                                ></div>
                                                {labels.find((l) => (l.id || l._id) === call.followupLabel)?.name ||
                                                    'Unknown'}
                                            </div>
                                        ) : (
                                            'Select follow-up label'
                                        )}
                                    </Dropdown.Toggle> */}
                                    {/* <Dropdown.Menu>
                                        {labels.map((label) => (
                                            <Dropdown.Item
                                                key={label.id || label._id}
                                                onClick={() => handleUpdateLabel(call.id, label.id || label._id)}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        style={{
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor: label.colorHex,
                                                            borderRadius: '50%',
                                                            marginRight: '8px',
                                                        }}
                                                    ></div>
                                                    {label.name}
                                                </div>
                                            </Dropdown.Item>
                                        ))}
                                        <Dropdown.Divider />
                                        <Dropdown.Item onClick={() => handleUpdateLabel(call.id, null)}>
                                            <div className="text-muted">Clear Label</div>
                                        </Dropdown.Item>
                                    </Dropdown.Menu> */}
                                </Dropdown>
                            </div>

                            {/* Call Actions */}

                            {/* Call Details */}
                            <div className="mb-3">
                                <Table size="sm" borderless>
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Call ID:</td>
                                            <td>{call.id || call._id}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">From number:</td>
                                            <td>{call?.fromNumber || '—'}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">To number:</td>
                                            <td>{call?.toNumber || '—'}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Started:</td>
                                            <td>{new Date(call.startedAt || call.createdAt).toLocaleString()}</td>
                                        </tr>
                                        {call.endedAt && (
                                            <tr>
                                                <td className="text-muted">Ended:</td>
                                                <td>{new Date(call.endedAt).toLocaleString()}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="text-muted">Duration:</td>
                                            <td>{formatDuration(call.durationSeconds)}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Preset:</td>
                                            <td>{call.presetData?.title || 'None'}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Called By:</td>
                                            <td>{call.dialedByUserData?.name || 'System'}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                            {/* Call Notes */}
                            <div className="mb-3 d-flex flex-row">
                                <CallCenterNotesModal callId={call?._id} callData={call} />
                                <OpenCallPageButton callId={call?._id} compactMode={false} />
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </OurAccordion>
        </Card>
    );
};

const mapStateToProps = (state) => ({
    contacts: state.callCenter.contacts || [],
    calls: state.callCenter.calls || [],
    totalCalls: state.callCenter.totalCalls,
    labels: state.callCenter.labels || [],
    presets: state.callCenter.presets || [],
});
const mapDispatchToProps = {
    fetchCallsAction,
    updateCallDataAction,
};

const CallAccordion = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(CallAccordion);
